import React, { useState } from 'react';
import './DataTable.css';
import {
	FaSort,
	FaSortDown,
	FaSortUp,
	FaToggleOff,
	FaToggleOn,
	FaUndo,
	SearchOrder
} from '@/Shared';

import { Header, SortedColumn } from '@/Shared/Types';
import DataFilter from '../DataFilter/DataFilter';
import { useDataTableContext } from '@/Shared/Logic/Contexts/DataTableContext';
import { RecordHelper } from '@/Shared/Helpers/record.helper';
import { ActionButton, CreateButton, ExportButton } from '../../Buttons';
import LoadMoreButton from '../../Buttons/LoadMoreButton/LoadMoreButton';


/**
 * Component: DataTable
 *
 * Description:
 * Generic, reusable DataTable component supporting:
 * - Sorting per column
 * - Filtering via a connected DataFilter component
 * - Pagination with load-more capability
 * - Excel export with confirmation modal
 * - Clickable rows and customizable action buttons
 *
 * Architecture / Intent:
 * - Uses `useDataTableContext` to centralize all table logic and state
 * - Minimal presentation logic here; mostly maps data from context to table UI
 * - Supports both icons and headers dynamically
 * - Fully responsive with CSS scroll and ellipsis handling
 *
 * Features:
 * - Sortable columns with visual ascending/descending indicators
 * - Optional row click handling
 * - Conditional rendering for empty state and load more
 * - Integration with CreateButton and ExportButton components
 * - ActionStatusHelper used to show loading state during exports
 *
 * Generics:
 * - <T>: Represents the shape of each row in the table (Record<string, any>)
 *
 * Author: Samano CASTRE
 * Date: June 29, 2025
 */
function DataTable<VM extends Record<string, any>>() {

	const {
		Icon,
		slicedData,
		isFilterable,
		processSort,
		headers,
		noStickyHeader,
		hasMoreData,
		batchCapacity,
		loadMoreData,
		processedDataCount,
		activeFilters,
		resetFilter,
		actionHandlers,
		domainMode,
		isRestorable,
		newItems,
		title,
		headerAction
	} = useDataTableContext<VM>();

	const [sort, setSort] = useState<SortedColumn<VM>>();

	/**
	 * Returns the proper sort icon element for a column header.
	 * Shows ascending, descending, or default icon based on the current sort state.
	 */
	const getSortIcon = (header: Header<VM>): JSX.Element => {

		if (!header.isSortable) return <></>;

		let icon = <FaSort className='sortable-icon' />;
		if (sort?.header === header) {
			icon = sort.order === 'asc' ?
				<FaSortUp className='sortable-icon ascending' /> :
				<FaSortDown className='sortable-icon descending' />;
		}

		return <button type="button" className='sortable-icon__button' aria-label="sort button">{icon}</button>;
	}

	/**
	 * Handles click on a sortable header:
	 * - Toggles sort order if the same header is clicked
	 * - Defaults to ascending otherwise
	 * - Updates local sort state and triggers the processSort callback from context
	 */
	const handleSort = (header: Header<VM>) => {
		let order = "asc" as SearchOrder;
		if (header === sort?.header) {
			order = sort.order === "asc" ? "desc" : "asc";
		}
		setSort({
			header,
			order
		});
		processSort(header.key, order);
	}

	const isActive = domainMode?.current === 'active';

	return (
		<div className="DataTable" data-testid="DataTable">

			{/* Sidebar with filters, export, create buttons */}
			<div className="Datatable-aside sm-scroll">
				{
					isFilterable &&
					(<DataFilter />)
				}

				<div className='DataTable-filter__buttons flex-row'>
					{
						/* Reset filters button */
						!RecordHelper.isEmpty(activeFilters) &&
						(<button
							className='reset-filter-button'
							title='Annuler tous les filtres'
							onClick={resetFilter}>
							<FaUndo /> Annuler filtres
						</button>)
					}
					{
						/* Optional create new row button */
						actionHandlers.handleCreate &&
						(
							<CreateButton onClick={actionHandlers.handleCreate} />
						)
					}
					{
						/* Excel export button */
						(processedDataCount > 0 && actionHandlers.handleExcelExport) &&
						(
							<ExportButton
								onClick={actionHandlers.handleExcelExport}
								count={processedDataCount}
							/>
						)
					}

				</div>
			</div>
			{/* Main table body */}
			<div className="DataTable__body sm-scroll">
				{(title || headerAction) && <div className='DataTable__header-wrapper'>
						{title && <h3 className='DataTable__title'>{title}</h3>}
						{headerAction}
					</div>
				}
				<table className={`data-table ${domainMode?.current ?? ''}`}>

					{
						domainMode && isRestorable &&
						<caption className="table-caption">
							<ActionButton
								classNames='toggle-button'
								onClick={domainMode.toggle}
								icon={isActive ? <FaToggleOn /> : <FaToggleOff />}
								label={isActive ? 'Afficher éléments supprimés' : 'Afficher éléments actifs'}
								isLoading={domainMode?.isLoading}
								showLabel
							/>
						</caption>
					}


					<thead className={noStickyHeader ? '' : 'sticky'}>
						<tr>
							<th className='datatable__icon'>{Icon && <Icon/>}</th>
							{
								RecordHelper.toValues(headers).filter(header => !header.isHidden).map(
									(header, index) => (
										<th
											onClick={() => handleSort(header)}
											key={String(header.key)}
											className={`th-${index} ${header.isSortable ? 'sortable' : ''}`}
											data-type={header.type}
											title={header.label}
										>
											<div className='ellipsis-2-lines'>
												{header.isSortable && getSortIcon(header)}

												{header.label}</div>
										</th>
									)
								)
							}
						</tr>
					</thead>
					<tbody>
						{
							/* Render rows */
							slicedData.map(
								(item, idx) => (
									<tr
										key={idx}
										onClick={() => actionHandlers.handleRead?.(item.id)}
										className={actionHandlers.handleRead ? 'clickable' : ''}
									>
										<td className={`datatable__icon layout_domain-icon ${(newItems ?? []).includes(item.id) ? 'is-new' : ''}`}>
											{Icon && <Icon/>}
										</td>
										{
											RecordHelper.toValues(headers)
												.filter(({ isHidden }) => !isHidden)
												.map(
													({ key, type }) => {
														const realType = item.getType?.(key) ?? type;
														const keyClass = item[String(key) + 'Class'];
														return (
															<td key={String(key)} data-type={realType} >
																<div
																	className={`ellipsis ${keyClass ? ('td-highlight ' + keyClass) : ''}`}
																	title={String(item[key])}>
																	{String(item[key])}
																</div>
															</td>
														)
													}
												)
										}
									</tr>
								)
							)
						}
						{
							/* Load more row */
							hasMoreData() && (
								<tr >
									<td
										className="load-more-container"
										colSpan={RecordHelper.size(headers)}>
										<LoadMoreButton
											batchCapacity={batchCapacity}
											onClick={loadMoreData}
										/>

									</td>
								</tr>
							)}
						{
							/* Empty state row */
							slicedData.length === 0 &&
							(
								<tr>
									<td
										className="datatable_no-result"
										colSpan={RecordHelper.size(headers) + 1}>
										Aucun résultat trouvé.
									</td>

								</tr>
							)
						}
					</tbody>
				</table>
			</div>
		</div>
	);
}

export default DataTable;